/**
 * Chat Store Core (Factory)
 *
 * Shared logic for the AI Chat Assistant store.
 * Both web and mobile create their own Zustand store by calling
 * `createChatSlice(deps)` and wrapping it with platform-specific
 * persistence (localStorage vs AsyncStorage).
 *
 * Conversations expire after 7 days of inactivity.
 */

import type { StateCreator } from 'zustand';
import { mergeDataCard } from '../chatDataCards';
import type {
  ChatConversationResponse,
  ChatMessage,
  ChatToolCallInfo,
  ChatToolErrorCategory,
  DataCard,
  GeneratePicksConfirmationDetails,
} from '../chatTypes';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max age for persisted conversations (7 days in ms) */
export const CONVERSATION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// State interface
// ---------------------------------------------------------------------------

export interface ChatState {
  isOpen: boolean;
  messages: ChatMessage[];
  isStreaming: boolean;
  conversationId: string | null;
  isLoadingConversation: boolean;
  unreadCount: number;
  quickChips: string[];
  lastActivityAt: number;
  pendingConfirmation: {
    confirmationId: string;
    action: string;
    details: string;
    structuredDetails?: GeneratePicksConfirmationDetails;
    toolCallId: string;
  } | null;
  /** True when agent is streaming follow-up text while a confirmation modal is open. */
  pendingFollowupActive: boolean;
  /** Bumped by resetLocal; a request that started under an older value writes nothing. */
  sessionEpoch: number;
  /** The in-flight message or confirm request, so resetLocal can abort it. */
  activeStreamAbort: AbortController | null;

  // Actions
  toggle: () => void;
  open: () => void;
  close: () => void;
  addMessage: (message: ChatMessage) => void;
  appendToLastMessage: (text: string) => void;
  addDataCardToLastMessage: (card: DataCard) => void;
  addToolCallToLastMessage: (toolCall: ChatToolCallInfo) => void;
  updateToolCallStatus: (
    toolCallId: string,
    status: ChatToolCallInfo['status'],
    errorCategory?: ChatToolErrorCategory,
  ) => void;
  /** Mark all 'running' tool calls in the last message as complete/error */
  finalizeRunningToolCalls: (status: 'complete' | 'error') => void;
  setStreaming: (streaming: boolean) => void;
  clearConversation: () => void;
  /** Empty the store for sign-out: no server request, unlike clearConversation's DELETE. */
  resetLocal: () => void;
  setActiveStreamAbort: (controller: AbortController | null) => void;
  setChips: (chips: string[]) => void;
  setPendingConfirmation: (confirmation: ChatState['pendingConfirmation']) => void;
  setPendingFollowupActive: (active: boolean) => void;
  incrementUnread: () => void;
  clearUnread: () => void;
  /** Replace older messages with a summary message after server compaction */
  applyCompaction: (summary: string, removedCount: number) => void;
  /** Set the server-assigned conversation ID */
  setConversationId: (id: string) => void;
  /** Load conversation from the server (cross-device resume) */
  loadFromServer: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Dependencies injected by each platform
// ---------------------------------------------------------------------------

export interface ChatStoreDeps {
  /** Platform-specific fetch wrapper (web: credentials:'include', mobile: authFetch) */
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check whether a conversation has expired based on lastActivityAt timestamp */
export function isConversationExpired(lastActivityAt: number): boolean {
  return Date.now() - lastActivityAt > CONVERSATION_MAX_AGE_MS;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createChatSlice(deps: ChatStoreDeps): StateCreator<ChatState> {
  return (set, get) => ({
    isOpen: false,
    messages: [],
    isStreaming: false,
    conversationId: null as string | null,
    isLoadingConversation: false,
    unreadCount: 0,
    quickChips: [],
    lastActivityAt: Date.now(),
    pendingConfirmation: null,
    pendingFollowupActive: false,
    sessionEpoch: 0,
    activeStreamAbort: null,

    toggle: () => {
      const { isOpen } = get();
      if (isOpen) {
        set({ isOpen: false });
      } else {
        set({ isOpen: true, unreadCount: 0 });
      }
    },

    open: () => set({ isOpen: true, unreadCount: 0 }),

    close: () => set({ isOpen: false }),

    addMessage: (message) =>
      set((state) => ({
        messages: [...state.messages, message],
        lastActivityAt: Date.now(),
      })),

    appendToLastMessage: (text) =>
      set((state) => {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        if (last && last.role === 'assistant') {
          messages[messages.length - 1] = { ...last, content: last.content + text };
        }
        return { messages, lastActivityAt: Date.now() };
      }),

    addDataCardToLastMessage: (card) =>
      set((state) => {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        if (last && last.role === 'assistant') {
          messages[messages.length - 1] = {
            ...last,
            dataCards: mergeDataCard(last.dataCards || [], card),
          };
        }
        return { messages };
      }),

    addToolCallToLastMessage: (toolCall) =>
      set((state) => {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        if (last && last.role === 'assistant') {
          messages[messages.length - 1] = {
            ...last,
            toolCalls: [...(last.toolCalls || []), toolCall],
          };
        }
        return { messages };
      }),

    updateToolCallStatus: (toolCallId, status, errorCategory) =>
      set((state) => {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        if (last && last.role === 'assistant' && last.toolCalls) {
          messages[messages.length - 1] = {
            ...last,
            toolCalls: last.toolCalls.map((tc) =>
              tc.id === toolCallId
                ? {
                    ...tc,
                    status,
                    completedAt: Date.now(),
                    errorCategory: errorCategory ?? tc.errorCategory,
                  }
                : tc,
            ),
          };
        }
        return { messages };
      }),

    finalizeRunningToolCalls: (status) =>
      set((state) => {
        const messages = [...state.messages];
        const last = messages[messages.length - 1];
        if (
          last &&
          last.role === 'assistant' &&
          last.toolCalls?.some((tc) => tc.status === 'running')
        ) {
          messages[messages.length - 1] = {
            ...last,
            toolCalls: last.toolCalls.map((tc) =>
              tc.status === 'running' ? { ...tc, status, completedAt: Date.now() } : tc,
            ),
          };
          return { messages };
        }
        return state;
      }),

    setStreaming: (streaming) => set({ isStreaming: streaming }),

    clearConversation: () => {
      set({
        messages: [],
        isStreaming: false,
        conversationId: null,
        quickChips: [],
        pendingConfirmation: null,
        pendingFollowupActive: false,
        lastActivityAt: Date.now(),
      });
      // Fire-and-forget server deletion
      deps.apiFetch('/api/chat/conversation', { method: 'DELETE' }).catch(() => {});
    },

    resetLocal: () => {
      get().activeStreamAbort?.abort();
      set((state) => ({
        isOpen: false,
        messages: [],
        isStreaming: false,
        conversationId: null,
        isLoadingConversation: false,
        unreadCount: 0,
        quickChips: [],
        lastActivityAt: Date.now(),
        pendingConfirmation: null,
        pendingFollowupActive: false,
        sessionEpoch: state.sessionEpoch + 1,
        activeStreamAbort: null,
      }));
    },

    setActiveStreamAbort: (controller) => set({ activeStreamAbort: controller }),

    setChips: (chips) => set({ quickChips: chips }),

    setPendingConfirmation: (confirmation) => set({ pendingConfirmation: confirmation }),

    setPendingFollowupActive: (active) => set({ pendingFollowupActive: active }),

    incrementUnread: () =>
      set((state) => ({
        unreadCount: state.isOpen ? 0 : state.unreadCount + 1,
      })),

    clearUnread: () => set({ unreadCount: 0 }),

    applyCompaction: (summary, removedCount) =>
      set((state) => {
        // Replace the first `removedCount` messages with a single summary message
        const kept = state.messages.slice(removedCount);
        const summaryMessage: ChatMessage = {
          id: `summary-${Date.now()}`,
          role: 'summary',
          content: summary,
          timestamp: Date.now(),
        };
        return { messages: [summaryMessage, ...kept] };
      }),

    setConversationId: (id) => set({ conversationId: id }),

    loadFromServer: async () => {
      const epoch = get().sessionEpoch;
      set({ isLoadingConversation: true });
      try {
        const response = await deps.apiFetch('/api/chat/conversation');
        if (!response.ok) return;

        const data: ChatConversationResponse = await response.json();
        if (get().sessionEpoch !== epoch) return;
        if (data.conversation && data.conversation.messages.length > 0) {
          set({
            messages: data.conversation.messages,
            conversationId: data.conversation.id,
            quickChips: data.conversation.quickChips,
            lastActivityAt: data.conversation.lastActivityAt,
          });
        }
      } catch {
        // Non-critical -- local store fallback is fine
      } finally {
        set({ isLoadingConversation: false });
      }
    },
  });
}
